import { useState, useEffect, useRef } from 'react';
import { usePlanningStore } from '@/stores/planning';
import { searchProjects } from '@/api/planning';
import type { ProjectSearchResult } from '@/api/planning';
import { FaseSelector } from './FaseSelector';

export function PlanningProjectSearch() {
  const showProjectSearch = usePlanningStore((s) => s.showProjectSearch);
  const setShowProjectSearch = usePlanningStore((s) => s.setShowProjectSearch);

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<ProjectSearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<ProjectSearchResult | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Auto-focus input when search opens
  useEffect(() => {
    if (showProjectSearch) {
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      setSearchQuery('');
      setSearchResults([]);
      setSearchOpen(false);
    }
  }, [showProjectSearch]);

  // Debounced search
  useEffect(() => {
    if (searchQuery.length < 3) {
      setSearchResults([]);
      setSearchOpen(false);
      return;
    }
    const timer = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const result = await searchProjects(searchQuery);
        setSearchResults(result.results);
        setSearchOpen(true);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Escape key closes search
  function cancelSearch() {
    setShowProjectSearch(false);
    usePlanningStore.setState({ scheduleTargetCell: null });
  }

  useEffect(() => {
    if (!showProjectSearch) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') cancelSearch();
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showProjectSearch]);

  // Click outside closes dropdown
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setSearchOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function handleResultClick(result: ProjectSearchResult) {
    setSearchOpen(false);
    setSearchQuery('');
    setSearchResults([]);
    setShowProjectSearch(false);
    setSelectedProject(result);
  }

  if (!showProjectSearch && !selectedProject) return null;

  return (
    <>
      {showProjectSearch && (
        <div ref={containerRef} className="sticky top-0 z-40 bg-blue-50 border-b border-blue-200 px-4 lg:px-8 pb-4 pt-4 shadow-md">
          <div className="flex items-center gap-3">
            <div className="w-2 self-stretch bg-blue-500 rounded-full shrink-0" />
            <span className="text-sm font-bold text-blue-700 shrink-0">Project inplannen</span>
            <div className="relative flex-1">
              <label htmlFor="pl-project-search" className="sr-only">Project zoeken</label>
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                id="pl-project-search"
                ref={inputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && searchResults.length === 1) {
                    handleResultClick(searchResults[0]);
                  }
                }}
                placeholder="Zoek project op naam of adres..."
                className="w-full pl-10 pr-4 py-2.5 text-sm border border-blue-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-td-blue/30 focus:border-td-blue"
              />
              {searchLoading && (
                <span className="absolute right-3 top-3 text-xs text-gray-400">...</span>
              )}
            </div>
            <button
              type="button"
              onClick={cancelSearch}
              className="shrink-0 p-1.5 text-blue-400 hover:text-blue-700 hover:bg-blue-100 rounded-lg transition-colors"
              aria-label="Sluiten"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>

          {/* Results dropdown */}
          {searchOpen && searchResults.length > 0 && (
            <div className="absolute left-4 right-4 lg:left-8 lg:right-8 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto">
              {searchResults.map((result) => (
                <button
                  key={result.id}
                  type="button"
                  onClick={() => handleResultClick(result)}
                  className="w-full text-left px-4 py-2.5 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 transition-colors"
                >
                  <p className="text-sm font-bold text-td-navy truncate">
                    {result.projectName || result.name}
                    {result.sale_order_name && (
                      <span className="ml-2 text-xs font-normal text-td-muted">{result.sale_order_name}</span>
                    )}
                  </p>
                  {result.address && (
                    <p className="text-xs text-td-muted truncate mt-0.5">{result.address}</p>
                  )}
                </button>
              ))}
            </div>
          )}

          {searchOpen && searchResults.length === 0 && searchQuery.length >= 3 && !searchLoading && (
            <div className="absolute left-4 right-4 lg:left-8 lg:right-8 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 p-4 text-sm text-gray-500">
              Geen projecten gevonden
            </div>
          )}
        </div>
      )}

      {/* Fase selector modal */}
      {selectedProject && (
        <FaseSelector
          projectId={selectedProject.id}
          projectName={selectedProject.projectName || selectedProject.name}
          address={selectedProject.address}
          onClose={() => { setSelectedProject(null); usePlanningStore.setState({ scheduleTargetCell: null }); }}
        />
      )}
    </>
  );
}
